import { PaginationCardMinimal } from "@admin-ui/components/application/pagination/pagination";
import { NativeSelect } from "@admin-ui/components/base/select/select-native";
import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Avatar } from "@admin-ui/components/primitives/base/avatar/avatar";
import { Badge, BadgeWithDot } from "@admin-ui/components/primitives/base/badges/badges";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { accountSetupStatusColors, formatSetupStatus } from "@admin-ui/utils/account-display";
import { formatDate } from "@admin-ui/utils/format-date";
import { formatPrice } from "@admin-ui/utils/format-price";
import { toSortDescriptor } from "@admin-ui/utils/sort";
import { adminListingPath } from "@travily/routes";
import type { AdminListingsListing, AdminListingsListingPhotoTrait } from "@travily/types/models";
import type AdminListingsIndex from "@travily/types/pages/AdminListingsIndex";
import { SearchLg, Tag01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { Link, TableBody } from "react-aria-components";

type ListingRow = AdminListingsListing & AdminListingsListingPhotoTrait;

export type ListingFilters = AdminListingsIndex["filters"];
export type ListingPagy = AdminListingsIndex["pagy"];

export interface IndexViewProps {
  listings: ListingRow[];
  pagy: ListingPagy;
  sort: string | null;
  sortDir: string | null;
  filters: ListingFilters;
  onSortChange: (descriptor: SortDescriptor) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: keyof ListingFilters, value: string) => void;
}

const TYPE_OPTIONS = [
  { label: "All types", value: "" },
  { label: "Product", value: "Product" },
  { label: "Service", value: "Service" },
];

const SETUP_STATUS_OPTIONS = [
  { label: "Any setup status", value: "" },
  { label: "Not started", value: "not_started" },
  { label: "In progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
];

export function IndexView({
  listings,
  pagy,
  sort,
  sortDir,
  filters,
  onSortChange,
  onPageChange,
  onSearchChange,
  onFilterChange,
}: IndexViewProps) {
  const sortDescriptor = toSortDescriptor(sort, sortDir);

  const [searchDraft, setSearchDraft] = useState(filters.search);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (searchDraft === filters.search) return;
    const timer = setTimeout(() => onSearchChange(searchDraft), 300);
    return () => clearTimeout(timer);
  }, [searchDraft, filters.search, onSearchChange]);

  return (
    <>
      <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4 ring-1 ring-secondary ring-inset md:flex-row md:flex-wrap md:items-end md:p-5">
        <div className="md:flex-1 md:min-w-64">
          <Input
            aria-label="Search listings"
            placeholder="Search by title or tagline"
            icon={SearchLg}
            value={searchDraft}
            onChange={setSearchDraft}
            size="sm"
          />
        </div>
        <NativeSelect
          aria-label="Type"
          size="sm"
          value={filters.listable_type}
          onChange={(e) => onFilterChange("listable_type", e.target.value)}
          options={TYPE_OPTIONS}
          className="md:w-40"
        />
        <NativeSelect
          aria-label="Setup status"
          size="sm"
          value={filters.setup_status}
          onChange={(e) => onFilterChange("setup_status", e.target.value)}
          options={SETUP_STATUS_OPTIONS}
          className="md:w-44"
        />
      </div>

      <TableCard.Root size="sm">
        <Table aria-label="Listings" sortDescriptor={sortDescriptor} onSortChange={onSortChange}>
          <Table.Header>
            <Table.Head id="title" label="Title" isRowHeader allowsSorting className="w-full max-w-1/3" />
            <Table.Head id="listable_type" label="Type" allowsSorting />
            <Table.Head id="account" label="Account" />
            <Table.Head id="setup_status" label="Setup" allowsSorting />
            <Table.Head id="price" label="Price" />
            <Table.Head id="created_at" label="Created" allowsSorting />
          </Table.Header>
          <TableBody items={listings} renderEmptyState={() => <EmptyState />}>
            {(listing: ListingRow) => (
              <Table.Row key={listing.id} id={listing.id}>
                <Table.Cell>
                  <Link
                    href={adminListingPath(listing.sqid)}
                    className="flex items-center gap-3 text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    <Avatar
                      src={listing.photo?.src ?? null}
                      alt={listing.title ?? "Listing"}
                      initials={(listing.title ?? "?").charAt(0).toUpperCase()}
                      size="sm"
                    />
                    <span className="flex flex-col">
                      <span className="whitespace-nowrap">{listing.title ?? "Untitled"}</span>
                      {listing.tagline && <span className="text-xs font-normal text-tertiary">{listing.tagline}</span>}
                    </span>
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={listing.listing_type === "Product" ? "brand" : "gray"} size="sm">
                    {listing.listing_type}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="flex flex-col">
                    <span className="text-sm">{listing.account_display_name ?? "—"}</span>
                    {listing.account_handle && <span className="text-xs text-tertiary">@{listing.account_handle}</span>}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <BadgeWithDot color={accountSetupStatusColors[listing.setup_status]} size="sm" type="pill-color">
                    {formatSetupStatus(listing.setup_status)}
                  </BadgeWithDot>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {formatPrice(listing.price_cents, listing.price_currency, listing.free)}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{formatDate(listing.created_at)}</Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      <PaginationCardMinimal align="right" page={pagy.page} total={pagy.pages} onPageChange={onPageChange} />
    </>
  );
}

function EmptyState() {
  return <TableEmptyState icon={Tag01} title="No listings match" description="Try clearing some filters." />;
}
